import { useState, type FormEvent } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/hooks/use-auth";
import { useI18n } from "@/i18n";
import { verifyEmail, resendVerification } from "@/api/endpoints";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AuthLangSwitcher } from "@/components/auth-lang-switcher";

export function RegisterPage() {
  const { isAuthenticated, register } = useAuth();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [venueName, setVenueName] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [venueNumber, setVenueNumber] = useState<number | null>(null);

  // Verification state
  const [verifyMode, setVerifyMode] = useState(false);
  const [code, setCode] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);

  if (isAuthenticated && !verifyMode) {
    return <Navigate to="/" replace />;
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await register({ name, email, phone, venueName, password });
      setVenueNumber(res.venueNumber);
      setVerifyMode(true);
    } catch (err) {
      // Backend açık bir hata cümlesi döndürdüyse (409 "e-posta kullanılıyor",
      // soft-delete açıklaması, validation flatten'ı, vs.) onu göster — generic
      // "Kayıt oluşturulamadı" mesajı yanıltıcıydı, kullanıcı gerçek sebebi
      // göremiyordu.
      const apiError = (err as { response?: { data?: { error?: unknown } } })
        ?.response?.data?.error;
      const msg = typeof apiError === "string" ? apiError : t("register_failed");
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await verifyEmail(email, code);
      navigate("/", { replace: true });
    } catch {
      setError(t("invalid_expired_code"));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    try {
      await resendVerification(email);
      setResendCooldown(60);
      const interval = setInterval(() => {
        setResendCooldown((c) => {
          if (c <= 1) { clearInterval(interval); return 0; }
          return c - 1;
        });
      }, 1000);
    } catch {
      // ignore
    }
  };

  // Verification step
  if (verifyMode) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
      <AuthLangSwitcher />
        <Card className="w-full max-w-sm">
          <CardHeader className="text-center">
            <CardTitle className="text-xl">{t("email_verification")}</CardTitle>
            {venueNumber && (
              <div className="rounded-lg bg-primary/10 px-4 py-3 my-2">
                <p className="text-xs text-muted-foreground">{t("your_venue_number")}</p>
                <p className="text-2xl font-bold text-primary tracking-wider">{venueNumber}</p>
                <p className="text-xs text-muted-foreground">{t("venue_number_note")}</p>
              </div>
            )}
            <p className="text-sm text-muted-foreground">
              {email} {t("email_verification_desc")}
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleVerify} className="space-y-4">
              <Input
                placeholder={t("enter_code")}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                maxLength={6}
                required
                className="text-center text-2xl tracking-widest"
              />
              {error && <p className="text-sm text-destructive">{error}</p>}
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? t("verifying") : t("verify")}
              </Button>
            </form>
            <div className="mt-4 text-center">
              <button
                className="text-sm text-muted-foreground hover:text-foreground disabled:opacity-50"
                onClick={handleResend}
                disabled={resendCooldown > 0}
              >
                {resendCooldown > 0 ? `${t("resend_code")} (${resendCooldown}s)` : t("resend_code")}
              </button>
            </div>
            <button
              className="mt-2 block w-full text-center text-sm text-muted-foreground hover:text-foreground"
              onClick={() => navigate("/", { replace: true })}
            >
              {t("verify_later")}
            </button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Registration form
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <AuthLangSwitcher />
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          <div className="mx-auto mb-2 flex size-12 items-center justify-center rounded-xl bg-primary">
            <span className="text-xl font-bold text-primary-foreground">H</span>
          </div>
          <CardTitle className="text-xl">HereMenu</CardTitle>
          <p className="text-sm text-muted-foreground">{t("register_subtitle")}</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              placeholder={t("venue_name_placeholder")}
              value={venueName}
              onChange={(e) => setVenueName(e.target.value)}
              required
            />
            <Input
              placeholder={t("owner_name")}
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
            <Input
              type="email"
              placeholder={t("email")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <Input
              type="tel"
              placeholder={t("phone_placeholder")}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required
            />
            <Input
              type="password"
              placeholder={t("password_min")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={6}
              required
            />
            {error && <p className="text-sm text-destructive">{error}</p>}
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? t("registering") : t("register")}
            </Button>
          </form>
          <p className="mt-4 text-center text-sm text-muted-foreground">
            {t("have_account")}{" "}
            <Link to="/login" className="font-medium text-primary hover:underline">
              {t("login")}
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
